/**
 * DeveloperSettings — Sprint 2 / TASK 2-1
 *
 * Lets a user mint and revoke personal API keys. The actual key infrastructure
 * lives in:
 *   • supabase/migrations/20260504000000_api_keys.sql      (table + RLS)
 *   • supabase/functions/api-key-generate/index.ts         (mint, returns raw)
 *   • supabase/functions/api-key-validate/index.ts         (verify Bearer key)
 *
 * Hard rules from TASK 2-1:
 *   • The raw key is shown EXACTLY ONCE after generation. The reveal modal is
 *     only dismissable after the user explicitly confirms "I've copied it".
 *   • The DB stores only sha256(raw_key) and the first-8-char prefix.
 *   • Revoke = DELETE the row (RLS policy api_keys_owner_delete).
 */
import {
  KeyRound,
  Plus,
  Copy,
  Check,
  Trash2,
  AlertTriangle,
  X as XIcon,
  Eye,
  EyeOff,
  Globe,
  ShieldAlert,
  Save,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { supabase } from '../../../lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ApiKeyRow {
  id: string;
  key_prefix: string;
  label: string | null;
  scopes: string[] | null;
  rate_limit_rpm: number | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

interface GenerateResponse {
  id: string;
  raw_key: string;
  key_prefix: string;
  label: string | null;
  scopes: string[];
  rate_limit_rpm: number;
  created_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'Never';
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 60) return 'Just now';
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86_400) return `${Math.floor(deltaSec / 3600)}h ago`;
  if (deltaSec < 86_400 * 30) return `${Math.floor(deltaSec / 86_400)}d ago`;
  return new Date(t).toLocaleDateString();
}

function formatAbsolute(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface DeveloperSettingsProps {
  /** When true, hides the External Search Credentials section (shown separately in AccountSettings) */
  hideExternalCreds?: boolean;
}

const DeveloperSettings: React.FC<DeveloperSettingsProps> = ({ hideExternalCreds = false }) => {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [generateOpen, setGenerateOpen] = useState<boolean>(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);

  // ── Load existing keys (RLS scopes to the current user) ───────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchErr } = await supabase
      .from('api_keys')
      .select('id, key_prefix, label, scopes, rate_limit_rpm, created_at, last_used_at, revoked_at')
      .is('revoked_at', null)
      .order('created_at', { ascending: false });

    if (fetchErr) {
      setError(fetchErr.message || 'Failed to load API keys.');
      setKeys([]);
    } else {
      setKeys((data ?? []) as ApiKeyRow[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ── Revoke (DELETE, RLS-gated to owner) ───────────────────────────────────
  const handleRevoke = useCallback(async (row: ApiKeyRow) => {
    setError(null);
    const { error: delErr } = await supabase
      .from('api_keys')
      .delete()
      .eq('id', row.id);
    if (delErr) {
      setError(delErr.message || 'Failed to revoke API key.');
      return;
    }
    setRevokeTarget(null);
    await refresh();
  }, [refresh]);

  // ── Render helpers ────────────────────────────────────────────────────────
  const hasKeys = !loading && keys.length > 0;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <header className="flex items-start gap-3">
          <div
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: 'var(--ds-brand-soft)',
              color: 'var(--ds-brand)',
            }}
          >
            <KeyRound className="w-5 h-5" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="t-h1" style={{ color: 'var(--ds-text-strong)' }}>
              API Keys
            </h1>
            <p className="mt-1 t-body t-muted">
              Personal access tokens for the GoGrabJob public API. Each key is
              tied to your account and inherits your plan&apos;s scopes and rate
              limits.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setGenerateOpen(true)}
            className="hidden sm:inline-flex shrink-0 items-center gap-2 min-h-[40px] px-3.5 rounded-xl t-body font-medium
              bg-[var(--ds-brand)] text-white hover:opacity-90 transition-opacity touch-manipulation"
          >
            <Plus className="w-4 h-4" aria-hidden />
            Generate new API key
          </button>
        </header>

        {/* Mobile-only generate button (under header) */}
        <button
          type="button"
          onClick={() => setGenerateOpen(true)}
          className="sm:hidden w-full inline-flex items-center justify-center gap-2 min-h-[44px] px-3.5 rounded-xl t-body font-medium
            bg-[var(--ds-brand)] text-white hover:opacity-90 transition-opacity touch-manipulation"
        >
          <Plus className="w-4 h-4" aria-hidden />
          Generate new API key
        </button>

        {/* ── External Search Credentials ─────────────────────────────── */}
        {!hideExternalCreds && <ExternalCredentials />}

        {/* ── Security callout ───────────────────────────────────────────── */}
        <div
          className="flex gap-3 p-4 rounded-xl border"
          style={{
            background: 'color-mix(in srgb, var(--ds-warn) 8%, transparent)',
            borderColor: 'color-mix(in srgb, var(--ds-warn) 25%, transparent)',
          }}
        >
          <ShieldAlert
            className="w-5 h-5 shrink-0 mt-0.5"
            style={{ color: 'var(--ds-warn)' }}
            aria-hidden
          />
          <div className="t-body leading-relaxed" style={{ color: 'var(--ds-text-strong)' }}>
            <strong className="font-semibold">Treat API keys like passwords.</strong>{' '}
            We hash and store the SHA-256 of every key — the raw value is shown
            only once at the moment of generation. If you lose a key, revoke it
            and create a new one.
          </div>
        </div>

        {/* ── Error banner ───────────────────────────────────────────────── */}
        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 p-3 rounded-lg t-body"
            style={{
              background: 'rgba(239,68,68,0.08)',
              color: 'var(--ds-danger)',
              border: '1px solid rgba(239,68,68,0.25)',
            }}
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
            <span>{error}</span>
          </div>
        )}

        {/* ── Key list ───────────────────────────────────────────────────── */}
        <section className="ds-card overflow-hidden" style={{ padding: 0 }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="sm" showText={false} />
              <span className="ml-2 t-body vscode-text-secondary">Loading keys…</span>
            </div>
          ) : !hasKeys ? (
            <EmptyState onGenerate={() => setGenerateOpen(true)} />
          ) : (
            <ul className="divide-y vscode-border-secondary" role="list">
              {keys.map((k) => (
                <KeyListItem
                  key={k.id}
                  row={k}
                  onRevoke={() => setRevokeTarget(k)}
                />
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* ── Generate modal ──────────────────────────────────────────────── */}
      {generateOpen && (
        <GenerateKeyModal
          onClose={() => setGenerateOpen(false)}
          onGenerated={async () => {
            // The list is refreshed once the user closes the reveal step.
            await refresh();
          }}
        />
      )}

      {/* ── Revoke confirmation ─────────────────────────────────────────── */}
      {revokeTarget && (
        <RevokeConfirmModal
          row={revokeTarget}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={() => handleRevoke(revokeTarget)}
        />
      )}
    </div>
  );
};

export default DeveloperSettings;

// ─────────────────────────────────────────────────────────────────────────────
// External Search Credentials — exported so AccountSettingsPage can embed it
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ggj_external_search_creds';

export interface ExternalCred {
  id: string;
  key: string;
  value: string;
  description?: string;
}

/** Decode a JWT payload and return expiry info if present */
export function getJwtExpiry(token: string): { exp: Date; expired: boolean } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return null;
    const exp = new Date(payload.exp * 1000);
    return { exp, expired: Date.now() > payload.exp * 1000 };
  } catch {
    return null;
  }
}

export function loadExternalCreds(): ExternalCred[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ExternalCred[]) : [];
  } catch {
    return [];
  }
}

function saveExternalCreds(creds: ExternalCred[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
}

const PRESET_KEYS: Array<{ key: string; description: string; placeholder: string }> = [
  { key: 'linkedin_li_at', description: 'LinkedIn li_at session cookie', placeholder: 'AQE...' },
  { key: 'naukri_nauk_at', description: 'Naukri nauk_at session token', placeholder: 'eyJ...' },
  { key: 'indeed_indeed_ssl', description: 'Indeed indeed-ssl cookie', placeholder: 'v2...' },
  { key: 'internshala_session', description: 'Internshala session token', placeholder: 'session...' },
];

const ExternalCredentials: React.FC = () => {
  const [creds, setCreds]       = useState<ExternalCred[]>(() => loadExternalCreds());
  const [editId, setEditId]     = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState('');
  const [draftVal, setDraftVal] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [showVals, setShowVals] = useState<Set<string>>(new Set());
  const [saved, setSaved]       = useState(false);

  const persist = (next: ExternalCred[]) => {
    saveExternalCreds(next);
    setCreds(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const startEdit = (c?: ExternalCred) => {
    if (c) {
      setEditId(c.id);
      setDraftKey(c.key);
      setDraftVal(c.value);
      setDraftDesc(c.description ?? '');
    } else {
      setEditId('new');
      setDraftKey('');
      setDraftVal('');
      setDraftDesc('');
    }
  };

  const startPreset = (preset: typeof PRESET_KEYS[number]) => {
    const existing = creds.find((c) => c.key === preset.key);
    if (existing) { startEdit(existing); } else {
      setEditId('new');
      setDraftKey(preset.key);
      setDraftVal('');
      setDraftDesc(preset.description);
    }
  };

  const commitEdit = () => {
    const k = draftKey.trim();
    const v = draftVal.trim();
    if (!k || !v) { setEditId(null); return; }
    if (editId === 'new') {
      persist([...creds, { id: crypto.randomUUID(), key: k, value: v, description: draftDesc || undefined }]);
    } else {
      persist(creds.map((c) => c.id === editId ? { ...c, key: k, value: v, description: draftDesc || undefined } : c));
    }
    setEditId(null);
  };

  const removeCred = (id: string) => persist(creds.filter((c) => c.id !== id));

  const toggleShow = (id: string) =>
    setShowVals((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(34,197,94,0.10)', color: '#16a34a' }}
          >
            <Globe className="w-4 h-4" aria-hidden />
          </div>
          <div>
            <h2 className="t-card-title font-semibold vscode-text-primary">External Search Credentials</h2>
            <p className="t-meta vscode-text-secondary">
              Session tokens/cookies used by the AI Agent in <strong>External</strong> mode.
              Stored locally on this device only.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => startEdit()}
          className="shrink-0 inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg t-body font-medium
            border vscode-border vscode-text-primary hover:vscode-bg-hover transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add credential
        </button>
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {PRESET_KEYS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => startPreset(p)}
            className={`inline-flex items-center gap-1 t-meta font-medium px-2.5 py-1 rounded-full border transition-colors ${
              creds.some((c) => c.key === p.key)
                ? 'border-green-400/40 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                : 'vscode-border vscode-text-secondary hover:vscode-bg-hover'
            }`}
          >
            {creds.some((c) => c.key === p.key) ? (
              <Check className="w-3 h-3" />
            ) : (
              <Plus className="w-3 h-3" />
            )}
            {p.key}
          </button>
        ))}
      </div>

      {/* Edit form */}
      {editId && (
        <div
          className="mb-4 p-4 rounded-xl border space-y-3"
          style={{ borderColor: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' }}
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 t-body">
              <span className="font-medium vscode-text-primary">Key</span>
              <input
                type="text"
                value={draftKey}
                onChange={(e) => setDraftKey(e.target.value)}
                placeholder="e.g. linkedin_li_at"
                className="min-h-[36px] px-3 rounded-lg border vscode-border vscode-bg-secondary vscode-text-primary
                  placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[var(--ds-brand)]"
              />
            </label>
            <label className="flex flex-col gap-1 t-body">
              <span className="font-medium vscode-text-primary">Value (token / cookie)</span>
              <input
                type="password"
                value={draftVal}
                onChange={(e) => setDraftVal(e.target.value)}
                placeholder="Paste your session token here"
                className="min-h-[36px] px-3 rounded-lg border vscode-border vscode-bg-secondary vscode-text-primary
                  placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[var(--ds-brand)]"
              />
            </label>
          </div>
          <label className="flex flex-col gap-1 t-body">
            <span className="font-medium vscode-text-primary">Description (optional)</span>
            <input
              type="text"
              value={draftDesc}
              onChange={(e) => setDraftDesc(e.target.value)}
              placeholder="e.g. LinkedIn session cookie for job search"
              className="min-h-[36px] px-3 rounded-lg border vscode-border vscode-bg-secondary vscode-text-primary
                placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[var(--ds-brand)]"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditId(null)}
              className="min-h-[36px] px-3 rounded-lg t-body border vscode-border vscode-text-primary hover:vscode-bg-hover">
              Cancel
            </button>
            <button type="button" onClick={commitEdit}
              disabled={!draftKey.trim() || !draftVal.trim()}
              className="min-h-[36px] px-3 rounded-lg t-body font-medium inline-flex items-center gap-1.5
                bg-[var(--ds-brand)] text-white disabled:opacity-50">
              <Save className="w-3.5 h-3.5" />
              Save
            </button>
          </div>
        </div>
      )}

      {/* Stored credentials list */}
      {creds.length === 0 ? (
        <div className="t-body vscode-text-tertiary py-3 px-1">
          No credentials stored. Add a session cookie/token above to enable external job search.
        </div>
      ) : (
        <ul className="space-y-2">
          {creds.map((c) => (
            <li key={c.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl border vscode-border vscode-bg-secondary">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="t-meta font-mono font-medium" style={{ color: 'var(--ds-text-strong)' }}>{c.key}</code>
                  {c.description && (
                    <span className="t-meta truncate" style={{ color: 'var(--ds-text-muted)' }}>{c.description}</span>
                  )}
                  {/* JWT expiry badge */}
                  {(() => {
                    const jwtInfo = getJwtExpiry(c.value);
                    if (!jwtInfo) return null;
                    const diffMs  = jwtInfo.exp.getTime() - Date.now();
                    const diffMin = Math.round(diffMs / 60000);
                    const label   = jwtInfo.expired
                      ? `Expired ${Math.abs(diffMin) < 60 ? Math.abs(diffMin) + 'm ago' : jwtInfo.exp.toLocaleDateString()}`
                      : diffMin < 60
                        ? `Expires in ${diffMin}m`
                        : `Expires ${jwtInfo.exp.toLocaleDateString()}`;
                    return (
                      <span className={`inline-flex items-center gap-1 t-tag font-medium px-1.5 py-0.5 rounded-full ${
                        jwtInfo.expired
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : diffMin < 60
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {jwtInfo.expired ? '⚠' : '✓'} {label}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <code className="t-meta font-mono truncate max-w-[300px]" style={{ color: 'var(--ds-text-body)' }}>
                    {showVals.has(c.id) ? c.value : '•'.repeat(Math.min(24, c.value.length))}
                  </code>
                  <button type="button" onClick={() => toggleShow(c.id)}
                    className="shrink-0 min-h-[24px] min-w-[24px] inline-flex items-center justify-center rounded hover:vscode-bg-hover vscode-text-tertiary">
                    {showVals.has(c.id) ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => startEdit(c)}
                  className="min-h-[32px] min-w-[32px] inline-flex items-center justify-center rounded-lg hover:vscode-bg-hover vscode-text-secondary t-body">
                  Edit
                </button>
                <button type="button" onClick={() => removeCred(c.id)}
                  className="min-h-[32px] min-w-[32px] inline-flex items-center justify-center rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {saved && (
        <p className="t-meta mt-2 flex items-center gap-1" style={{ color: 'var(--ds-ok)' }}>
          <Check className="w-3 h-3" /> Credentials saved locally.
        </p>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ onGenerate: () => void }> = ({ onGenerate }) => (
  <div className="flex flex-col items-center justify-center text-center px-6 py-12 gap-3">
    <div
      className="w-12 h-12 rounded-2xl flex items-center justify-center"
      style={{
        background: 'var(--ds-brand-soft)',
        color: 'var(--ds-brand)',
      }}
    >
      <KeyRound className="w-6 h-6" aria-hidden />
    </div>
    <h2 className="t-card-title font-semibold vscode-text-primary">No API keys yet</h2>
    <p className="t-body vscode-text-secondary max-w-sm">
      Generate your first key to start using the GoGrabJob API. You can revoke
      it any time from this page.
    </p>
    <button
      type="button"
      onClick={onGenerate}
      className="mt-2 inline-flex items-center gap-2 min-h-[40px] px-3.5 rounded-xl t-body font-medium
        bg-[var(--ds-brand)] text-white hover:opacity-90 transition-opacity touch-manipulation"
    >
      <Plus className="w-4 h-4" aria-hidden />
      Generate new API key
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Single row in the key list
// ─────────────────────────────────────────────────────────────────────────────

const KeyListItem: React.FC<{ row: ApiKeyRow; onRevoke: () => void }> = ({
  row,
  onRevoke,
}) => {
  const scopes = row.scopes ?? [];
  return (
    <li className="px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
      <div className="min-w-0 flex-1 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold vscode-text-primary truncate">
            {row.label || 'Untitled key'}
          </span>
          <code
            className="shrink-0 t-meta font-mono px-1.5 py-0.5 rounded"
            style={{ background: 'var(--ds-bg-subtle)', color: 'var(--ds-text-body)' }}
            title="Key prefix"
          >
            {row.key_prefix}…
          </code>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 t-meta vscode-text-secondary">
          <span>Created {formatAbsolute(row.created_at)}</span>
          <span aria-hidden>·</span>
          <span>Last used {formatRelative(row.last_used_at)}</span>
          <span aria-hidden>·</span>
          <span>{row.rate_limit_rpm ?? 60} req/min</span>
        </div>
        {scopes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {scopes.map((scope) => (
              <span
                key={scope}
                className="t-meta font-medium px-2 py-0.5 rounded-full"
                style={{
                  background: 'var(--ds-brand-soft)',
                  color: 'var(--ds-brand)',
                }}
              >
                {scope}
              </span>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRevoke}
        className="shrink-0 inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg t-body font-medium
          border vscode-border vscode-text-primary hover:bg-[rgba(239,68,68,0.08)] hover:text-[#EF4444]
          hover:border-[rgba(239,68,68,0.4)] transition-colors touch-manipulation"
        aria-label={`Revoke ${row.label || 'API key'}`}
      >
        <Trash2 className="w-4 h-4" aria-hidden />
        Revoke
      </button>
    </li>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Generate modal — two phases:
//   1. Label entry   → "Generate"
//   2. Reveal raw    → must explicitly confirm "I've copied it" to close
// ─────────────────────────────────────────────────────────────────────────────

const GenerateKeyModal: React.FC<{
  onClose: () => void;
  onGenerated: () => Promise<void>;
}> = ({ onClose, onGenerated }) => {
  type Phase = 'form' | 'submitting' | 'reveal';
  const [phase, setPhase] = useState<Phase>('form');
  const [label, setLabel] = useState<string>('');
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [rawKey, setRawKey] = useState<string>('');
  const [keyPrefix, setKeyPrefix] = useState<string>('');
  const [keyLabel, setKeyLabel] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [reveal, setReveal] = useState<boolean>(true);

  // Escape closes the modal — but ONLY in the form phase. During reveal we
  // require explicit confirmation per the security contract.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (phase === 'reveal') return;
      if (phase === 'submitting') return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [phase, onClose]);

  const handleSubmit = useCallback(async () => {
    setSubmitErr(null);
    setPhase('submitting');
    try {
      const { data, error } = await supabase.functions.invoke<GenerateResponse>(
        'api-key-generate',
        { body: { label: label.trim() || undefined } },
      );
      if (error) throw new Error(error.message || 'Could not create key.');
      if (!data?.raw_key) throw new Error('Server did not return a key.');
      setRawKey(data.raw_key);
      setKeyPrefix(data.key_prefix);
      setKeyLabel(data.label);
      setPhase('reveal');
    } catch (e) {
      setSubmitErr(e instanceof Error ? e.message : 'Could not create key.');
      setPhase('form');
    }
  }, [label]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [rawKey]);

  const handleConfirmedClose = useCallback(async () => {
    if (!confirmed) return;
    // Best-effort: scrub the in-memory key value before unmounting.
    setRawKey('');
    await onGenerated();
    onClose();
  }, [confirmed, onClose, onGenerated]);

  return (
    <ModalShell
      onBackdropClick={() => {
        // Backdrop dismiss is allowed in the form phase only — never during
        // reveal, so the user cannot accidentally lose the one-time key.
        if (phase === 'form') onClose();
      }}
      title={phase === 'reveal' ? 'Copy your API key' : 'Generate API key'}
      onCloseClick={phase === 'form' ? onClose : undefined}
    >
      {phase !== 'reveal' ? (
        <div className="flex flex-col gap-4">
          <p className="t-body vscode-text-secondary">
            Give your key a memorable label so you can identify it later.
          </p>
          <label className="flex flex-col gap-1.5 t-body">
            <span className="font-medium vscode-text-primary">Label</span>
            <input
              type="text"
              value={label}
              maxLength={80}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Production server"
              autoFocus
              disabled={phase === 'submitting'}
              className="w-full min-h-[40px] px-3 rounded-lg border vscode-border vscode-bg-secondary
                vscode-text-primary placeholder:vscode-text-tertiary
                focus:outline-none focus:ring-2 focus:ring-[var(--ds-brand)] focus:border-[var(--ds-brand)]
                transition-colors"
            />
            <span className="t-meta" style={{ color: 'var(--ds-text-muted)' }}>
              Optional. Up to 80 characters.
            </span>
          </label>

          {submitErr && (
            <div
              role="alert"
              className="flex items-start gap-2 p-3 rounded-lg t-body"
              style={{
                background: 'rgba(239,68,68,0.08)',
                color: 'var(--ds-danger)',
                border: '1px solid rgba(239,68,68,0.25)',
              }}
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
              <span>{submitErr}</span>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={phase === 'submitting'}
              className="min-h-[40px] px-3.5 rounded-lg t-body font-medium
                border vscode-border vscode-text-primary hover:vscode-bg-hover
                disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={phase === 'submitting'}
              className="min-h-[40px] px-3.5 rounded-lg t-body font-medium inline-flex items-center justify-center gap-2
                bg-[var(--ds-brand)] text-white hover:opacity-90
                disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
            >
              {phase === 'submitting' && (
                <LoadingSpinner size="xs" showText={false} />
              )}
              Generate
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div
            className="flex gap-3 p-3 rounded-xl border"
            style={{
              background: 'color-mix(in srgb, var(--ds-warn) 8%, transparent)',
              borderColor: 'color-mix(in srgb, var(--ds-warn) 25%, transparent)',
            }}
          >
            <ShieldAlert
              className="w-5 h-5 shrink-0 mt-0.5"
              style={{ color: 'var(--ds-warn)' }}
              aria-hidden
            />
            <div className="t-body leading-relaxed" style={{ color: 'var(--ds-text-strong)' }}>
              <strong className="font-semibold">Copy this key now.</strong>{' '}
              You will not see this key again — it&apos;s shown only once. If
              you lose it, revoke this entry and generate a new one.
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="t-body font-medium vscode-text-primary">
                {keyLabel || 'Untitled key'}{' '}
                <span className="vscode-text-tertiary font-normal">({keyPrefix}…)</span>
              </span>
              <button
                type="button"
                onClick={() => setReveal((v) => !v)}
                className="inline-flex items-center gap-1 t-meta vscode-text-secondary hover:vscode-text-primary
                  min-h-[32px] px-2 rounded-md hover:vscode-bg-hover transition-colors"
                aria-label={reveal ? 'Hide key' : 'Show key'}
              >
                {reveal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {reveal ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="relative">
              <code
                className="block w-full break-all p-3 pr-12 rounded-lg font-mono t-meta
                  vscode-bg-tertiary vscode-text-primary border vscode-border select-all"
                aria-label="API key"
              >
                {reveal ? rawKey : '•'.repeat(Math.max(24, rawKey.length))}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className="absolute right-2 top-1/2 -translate-y-1/2 min-h-[36px] min-w-[36px]
                  inline-flex items-center justify-center rounded-md hover:vscode-bg-hover transition-colors"
                aria-label="Copy key to clipboard"
              >
                {copied ? (
                  <Check className="w-4 h-4" style={{ color: 'var(--ds-ok)' }} />
                ) : (
                  <Copy className="w-4 h-4 vscode-text-secondary" />
                )}
              </button>
            </div>
            {copied && (
              <span className="t-meta" style={{ color: 'var(--ds-ok)' }}>
                Copied to clipboard.
              </span>
            )}
          </div>

          <label className="flex items-start gap-2 t-body cursor-pointer select-none">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[var(--ds-brand)]"
            />
            <span className="vscode-text-primary">
              I&apos;ve copied this key and stored it somewhere safe.
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleConfirmedClose}
              disabled={!confirmed}
              className="min-h-[40px] px-3.5 rounded-lg t-body font-medium
                bg-[var(--ds-brand)] text-white hover:opacity-90
                disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Revoke confirmation
// ─────────────────────────────────────────────────────────────────────────────

const RevokeConfirmModal: React.FC<{
  row: ApiKeyRow;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ row, onCancel, onConfirm }) => {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, submitting]);

  return (
    <ModalShell
      onBackdropClick={() => {
        if (!submitting) onCancel();
      }}
      title="Revoke API key"
      onCloseClick={!submitting ? onCancel : undefined}
    >
      <div className="flex flex-col gap-4">
        <p className="t-body vscode-text-primary">
          Revoke <strong>{row.label || 'this key'}</strong>{' '}
          (<code className="t-meta">{row.key_prefix}…</code>)? Any client using
          this key will start receiving 401 responses immediately. This action
          cannot be undone.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="min-h-[40px] px-3.5 rounded-lg t-body font-medium
              border vscode-border vscode-text-primary hover:vscode-bg-hover transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setSubmitting(true);
              onConfirm();
            }}
            disabled={submitting}
            className="min-h-[40px] px-3.5 rounded-lg t-body font-medium inline-flex items-center justify-center gap-2
              bg-[#EF4444] text-white hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {submitting && <LoadingSpinner size="xs" showText={false} />}
            Revoke key
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal shell
// ─────────────────────────────────────────────────────────────────────────────

const ModalShell: React.FC<{
  title: string;
  children: React.ReactNode;
  onBackdropClick: () => void;
  onCloseClick?: () => void;
}> = ({ title, children, onBackdropClick, onCloseClick }) => {
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Stable id used internally — useMemo keeps it referentially stable across
  // re-renders without pulling in any extra hooks beyond what's already in
  // module scope.
  const titleId = useMemo(
    () => `modal-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onBackdropClick}
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      />
      <div
        className="relative w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto
          vscode-bg-primary border vscode-border shadow-2xl
          rounded-t-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between gap-2 px-4 sm:px-5 h-12 border-b shrink-0"
          style={{ borderColor: 'var(--ds-border-strong)' }}
        >
          <h2 id={titleId} className="t-body font-semibold vscode-text-primary truncate">
            {title}
          </h2>
          {onCloseClick && (
            <button
              type="button"
              aria-label="Close"
              onClick={onCloseClick}
              className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded-lg hover:vscode-bg-hover transition-colors"
            >
              <XIcon className="w-4 h-4 vscode-text-primary" aria-hidden />
            </button>
          )}
        </div>
        <div className="p-4 sm:p-5">
          {children}
        </div>
      </div>
    </div>
  );
};

/** Named export for use in AccountSettingsPage (admin-only tab) */
export { ExternalCredentials as ExternalCredentialsSection };
